/** Shared zod primitives. Every request body/filter is validated with these. */

import { z } from "zod";
import {
  DEFAULT_PAGE_SIZE,
  EMAIL_PATTERN,
  MAX_PAGE_SIZE,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  PHONE_PATTERN,
  SKU_PATTERN,
  SLUG_MAX_LENGTH,
  SLUG_MIN_LENGTH,
  SLUG_PATTERN,
} from "../constants/config";

export const uuidSchema = z.uuid();

export const slugSchema = z
  .string()
  .trim()
  .min(SLUG_MIN_LENGTH)
  .max(SLUG_MAX_LENGTH)
  .regex(SLUG_PATTERN, "Use lowercase letters, numbers and single hyphens.");

export const nameSchema = z.string().trim().min(NAME_MIN_LENGTH).max(NAME_MAX_LENGTH);

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .regex(EMAIL_PATTERN, "Enter a valid email address.");

export const phoneSchema = z.string().trim().regex(PHONE_PATTERN, "Enter a valid phone number.");

export const skuSchema = z.string().trim().regex(SKU_PATTERN, "Use letters, numbers, dot, dash or underscore.");

export const moneySchema = z.coerce.number().finite().nonnegative();
export const positiveMoneySchema = z.coerce.number().finite().positive();

export const pageQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});

export const isoDateTimeSchema = z.iso.datetime({ offset: true });

export const dateRangeSchema = z
  .object({
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
  })
  .transform((range) => ({
    from: range.from ?? null,
    to: range.to ?? null,
  }));

export const trimmedNullableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((value) => (value && value.length > 0 ? value : null));

export const optionalIsoDateTimeSchema = z
  .string()
  .trim()
  .optional()
  .transform((value) => (value && value.length > 0 ? value : null));
